/**
 * Feature Flag IDs (GUIDs) — keyed by the same PascalCase names used throughout the LOS frontend.
 * The configuration API now returns feature flags keyed by ID instead of Name,
 * making the frontend immune to feature flag renames in the database.
 */
export const FeatureFlagIds = {
  AddCarSalesPriceComment: "5B78F447-2263-43BB-B54A-147538E62A93",
  AllowAddVehicleInLOS: "DFAE391C-9C61-4F04-A505-55A08417244E",
  AllowCalculatedTierOverride: "872562FF-147E-4DD4-824D-3331BD602721",
  AllowCoApplicantDelete: "980537B3-BA88-4613-A2D3-0126687B8A41",
  AllowCustomerPortalLinkCreation: "DA6D33FD-33C7-4456-9011-EC8146090776",
  AllowDuplicateContacts: "DAC22F92-6189-4155-9D86-0C267C517A26",
  AllowPaymentEdit: "467D8606-9D1E-4F07-B4F9-733EC00DEA73",
  AllowRateEdit: "CC01597A-D0DE-4CB0-B2F1-37FCE0025098",
  AllowTermEdit: "8B31B2AD-EB14-41EE-8BF8-2C43BDD571FB",
  AllowTradeInWithoutVehicleOfSale: "B2F4A8C1-3D6E-4F7A-9B5C-8E1D0F2A3B4C",
  AllowVehicleFullEdit: "2DBF4D4F-DE48-4D36-810F-12192B4D137F",
  ApplicationRehashes: "9BFD99E9-B6EA-4AC0-BD24-338CE8E0519B",
  Budget: "F5BE8A06-FB80-4689-949B-C96F500E6EE9",
  CanAddStipulations: "EA11A0DD-5E3D-41B1-85D7-F7C2350387FB",
  CanCreateNewApp: "3A053500-76E8-466E-A0A2-7812F93FB2B3",
  CanRaiseExceptions: "751F06EB-7ACB-4281-9BD1-3DAC7EB13E98",
  CanViewCustomFieldsTab: "5F7B0725-5DA1-4319-B6F1-C34B937803B2",
  CanViewQuoterScreen: "3784DA75-4EF8-4CCC-895C-7661EA69BB0B",
  CBD: "CA3B437B-6A88-49AC-B330-4B72DC0F12D5",
  ClearAllApplicationQueueFiltersWhenOpeningQueueComponent: "5C5B1AF3-BF85-4102-8FD1-BBCA1E3A5D30",
  ClientPostsLoansDirectlyToAms: "5D5CD178-32CF-4CD9-AEB0-F197F915FBC5",
  CopyApplication: "71EDB110-0527-4F7D-8433-BCEAE2939224",
  CPIAutoAccrueCheckedByDefault: "98CAE08A-A535-4F48-9E94-DCF5DF712CE5",
  CustomerWillAcknowledgeCreditApplicationInformation: "71B4CF17-8F81-416A-BD1C-1E995003AFCB",
  /**
   * @deprecated No longer read anywhere. The LOS gates dealer-service history, the decision
   * info card, and the profit calculator on `isIndirectApplication(currentApp)` instead.
   * Kept so the id still resolves for clients that have the row (e.g. CFI); do not add new usages.
   */
  DealerTrack: "17A5EC37-63A7-4256-A9DA-7018C7885FAA",
  DefaultToUnderwriting: "F1945D29-341A-4A25-B861-22C76FE8DEFB",
  DefaultVehicleLocation: "DCBB90C0-DDA6-44AC-80EA-67090EADAFB8",
  DisableDeskingIfRequiredStipsIncomplete: "C121F838-24B6-4CB3-B96D-544FAF7C3C12",
  DisableVehicleOnQuote: "5B5DA108-6E14-493C-B916-36E43BD8A36F",
  DisplayLocationDescription: "918A2A80-31FD-4E72-8A4C-58D7628482EA",
  DisplayNotesOnOverview: "00DCC102-7DB4-41F2-A47F-9B5D3206AE12",
  DontTieFinanceQuoteDeferredDownPaymentsToFirstPaymentDate: "03027BC7-2D04-4ECD-94F5-F83746DDC408",
  ExcludePaymentFromDebtToIncome: "3CF347C6-1571-447C-91B2-57333E47A045",
  ExcludeStockNumbers: "5F4DFBA5-9477-4AE6-A4CF-619A702A3BF1",
  FinicityEnabled: "FC657D3F-EFFE-44B0-A5B5-A19915D42978",
  HideApplicationLink: "B840E848-F09C-42ED-9372-6351A41E1604",
  HideBureauTab: "6E44519D-79B6-42D4-A734-3364FDDD4F59",
  HideCopyCalculatedQuoteIndirectDecisionBox: "BD61D623-C926-4888-AEC6-353CAD5FB9A6",
  HideCreditQualityAndRiskMetricsCardLOS: "4883D677-6897-4869-93EF-C06FE6E01539",
  HideDashboardRoute: "623E78D6-5A6B-4D4A-BFB8-E8D91AF64ECD",
  HideDownloadAppButton: "D8C7D222-64E7-4B92-B9DC-5D3407E991E5",
  HideEmploymentType: "DA6579A2-89E4-427E-9495-6FFDAC1FEAB8",
  HideMapLOS: "E55FD304-8F4D-4B5A-8A1C-DF2F049F1380",
  HidePrintButtonIndirectDecisionBox: "6CEE1E23-768B-4B8E-873D-DAEB4A164E06",
  HideQueueSalespersonColumn: "9F16B4D5-8C85-4637-9ABE-AEBC26156850",
  HideQueueScoreColumn: "469D49B8-72EB-4569-8CCB-D114EC3998A0",
  HideQueueSourceColumn: "B1CAE257-E49E-40CA-97A0-29B6E7109565",
  HideQueueStockNumberColumn: "6F073BD3-04B7-4D46-AB34-B376CB74DBE0",
  HideQueueVehicleLocationColumn: "585F97D2-DF95-4FC4-9410-F0D4B67421EA",
  HideSensitiveInfoOnDeskingSheet: "7087C14A-E1A7-44D1-9C8C-D3C08EEBAFD6",
  HideUnderwriter: "0756B6A7-5AFD-48AF-80FC-7DEBD7028AFF",
  IsBothDirectAndIndirect: "A386C6BA-F679-4EE7-A112-C454A91113EC",
  MinimumMonthlyIncome: "6E78C296-83FE-47E8-A066-706ADA4FA38E",
  NetIncomeLabelNameCreditApp: "775286CA-7FEB-4E9F-A6B1-737BFD443A2B",
  OverrideBusinessRules: "B3A4B2D5-A8A7-4EA5-90C1-D325AA6A3BC0",
  PreventSalesFromEditingApplicant: "51DFF5E2-9B88-4D10-9694-429FADB81DB1",
  PreventSalesFromEditingBudget: "73F81E77-6539-4627-A424-67F8FE3529A6",
  /**
   * The client prints loan documents inside the LOS for indirect deals. Combined with
   * `ApplicationQuoteType.Indirect` this is what the removed `IndirectWithInternalDocuments` (4)
   * quote type used to mean — use `usesInternalDocumentsForIndirect()` from `utils/quoteUtils`.
   */
  PrintDocumentsForIndirectDeals: "0A9BEA53-F407-45C2-9AFA-70391677DA07",
  PushReferencesFromUi: "1E82B04E-BC17-4E0D-A862-6EDAF6743073",
  QuickQualifyCreditPullAvailable: "E716EE28-C3A4-4F76-8242-01D97F9D7F40",
  RunProfitCalculatorAfterCarletonQuoteIsRun: "6ABE7999-9F3F-4BBD-B0B2-835DCF1EB039",
  Scienaptic: "D25CB960-5B2D-4713-8EB5-DD1878894502",
  SecureCloseEnabled: "A1D2E3F4-5678-4A9B-CDEF-0123456789AB",
  SetDefaultUnderwriterOnApp: "C38BA817-810D-43C7-87BD-0B19EECDBB1D",
  ShouldValidateAddress: "B5575DF6-CBE0-4C20-A754-AF593097CDBD",
  ShouldValidateSSN: "B9D542BB-20D3-48F9-8A66-A50BA2724483",
  ShowBiWeeklyTerm: "4D225E08-2878-4BBE-8B56-F90698CCEED8",
  ShowModifiedDateColumn: "601A199E-A90D-42D7-8B46-D857722D5226",
  ShowMissingStipulations: "57EE7949-F4A4-4406-BEB4-E69A26991C36",
  ShowPricingProgram: "046D5654-55CA-4FC9-8070-67130F9975B0",
  ShowQueueAssigneeColumn: "1C2A3EA3-2B29-4D9E-B26F-F8B3874AC4DB",
  SortMyWorkQueueForUnderwritersByApplicationStatusDate: "FD8A9338-6E0E-41F7-86FB-164860BDD22B",
  TakeDownPayments: "85D3E04D-BE96-4EEE-97FA-6372AF820569",
  TextMessages: "2115CE9F-C0F5-47C9-A8AD-8551B7FE803B",
  TypeOfVehicleRv: "00952914-488A-400A-B029-66EBE155DB72",
  Use45DayFirstPayment: "A784FD25-0F43-4FD5-8404-8A44DC0EF59F",
  UseAMSScoring: "58CB97AB-402C-43CB-BF94-E1C94959DEE8",
  UseApplicantQueueColumn: "9781EAC4-87D3-4D09-8D48-456FDBE5408F",
  UseApplicationReportOnly: "9F93B806-65B0-4FFC-B11A-B6C0BF335041",
  UseBusinessRulePaymentCalculation: "50D3AC4E-E68D-4E25-8950-8F4B71B85460",
  UseCarTheoInsteadOfNada: "28B84492-BF27-45F6-8ED9-D5015DFBF752",
  UseCheckMyDriver: "255F15C0-0E65-47CB-B247-9F4398575357",
  UseCommission: "B08A0131-E1EC-4EE5-AD5A-E71B6BA908F7",
  UseCustomizableOverviewScreen: "BEA60074-44AA-4A31-8D47-64D32C03D1C7",
  UseDealerReserve: "90625997-37BD-4DFF-A82A-4F66CE79BE72",
  UseExternalDecisionEngine: "5E0FB541-677B-45E8-BCCE-E6C5D5C5AC0F",
  UseInternalAmsScore: "55A12DC1-9876-4D13-856F-CA4F5606F734",
  UseJDPower: "CAB34D80-AB04-47E2-910B-5EF9E244020F",
  UseJDPowersport: "1620AFB1-1660-4ABB-9EA4-602EB6F04249",
  UseLOSChat: "DF68DFFB-74E1-4549-A446-9DA755ADFB81",
  UseMultiplePaymentStreamsWithAnyLoanClass: "F86E4C14-52C9-4549-87F2-5EF4CE8D2590",
  UseRetailDealerFlow: "C6A49805-E03C-4E90-9704-60A974EEFA45",
  UseVehicleInQuoteByDefaultWhenVehicleChanges: "125FC870-ADAB-4988-80F1-62D377426064",
  UsesFakeSsns: "A1C2D3E4-F5A6-47B8-C9D0-E1F2A3B4C5D6",
  WorkNumber: "2EA090C0-9090-483E-913E-8B576787AFD4",
  ZohoSignEnabled: "B8A9C0D1-E2F3-4A5B-6C7D-8E9F0A1B2C3D",
} as const;

export type FeatureFlagId = typeof FeatureFlagIds[keyof typeof FeatureFlagIds];
